/**
 * Created by jonas on 2021-02-24.
 */


import * as PIXI from "pixi.js";
import {BS2} from "../BS2";

export class MathUtils {

    /**
     * Maps value x in range inStart -> inEnd to range outStart -> outEnd.
     *
     * @param x Value to map to new range. x is a value in the in range.
     * @param inStart Start value in in range.
     * @param inEnd End value in in range.
     * @param outStart Start value in out range.
     * @param outEnd   End value in out range.
     * @returns {number}
     */
    static map(x:number, inStart:number, inEnd:number, outStart:number, outEnd:number):number {
        return (x - inStart) * (outEnd - outStart) / (inEnd - inStart) + outStart;
    }


    /**
     *
     * @param a
     * @param b
     * @param o [optional] Origin Set a different origin
     */
    public static dotProduct(a:PIXI.Point, b:PIXI.Point, o:PIXI.Point = new PIXI.Point()) {
        return (a.x - o.x) * (b.x - o.x) + (a.y - o.y) * (b.y - o.y);
    }

    /**
     *
     * @param a
     * @param b
     * @param o [optional] Origin Set a different origin
     */
    public static crossProduct(a:PIXI.Point, b:PIXI.Point, o:PIXI.Point = new PIXI.Point()) {
        return (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
    }


    /**
     * Returns witch quadrant the angle is in
     *
     * 1 -> x+ y+ //In pixi terms: South East
     * 2 -> x- y+ //In pixi terms: South West
     * 3 -> x- y- //In pixi terms: North West
     * 4 -> x+ y- //In pixi terms: North East
     * 0 -> flat
     *
     * @param angle
     * @param threshold Threshold angle
     */
    static getQuadrantForAngle(angle:number, threshold:number = 0):number {
        let sin = Math.sin(angle);
        let cos = Math.cos(angle);

        if(Math.abs(sin) < threshold) {
            sin = 0;
        }
        if(Math.abs(cos) < threshold) {
            cos = 0;
        }

        if(sin > 0 && cos > 0) {
            return 1;
        }

        if(sin > 0 && cos < 0) {
            return 2;
        }

        if(sin < 0 && cos < 0) {
            return 3;
        }

        if(sin < 0 && cos > 0) {
            return 4;
        }

        return 0;

    }


    static positiveAngle(angle:number):number {
        return (angle + Math.PI * 2) % (Math.PI * 2);
    }


    static vectorFromTwoPoints(origin:PIXI.Point, terminus:PIXI.Point):PIXI.Point {
        return MathUtils.subPoints(terminus, origin);
    }

    /**
     * Returns angle between two points in radians.
     * @param p1 first point
     * @param p2 second point
     * @returns {number} Angle
     */
    static angleBetweenPoints(p1:PIXI.Point, p2:PIXI.Point) {
        return Math.atan2(p2.y - p1.y, p2.x - p1.x);
    }

    static addPoints(p1:PIXI.Point, p2:PIXI.Point):PIXI.Point {
        return new PIXI.Point(p1.x + p2.x, p1.y + p2.y);
    }

    static subPoints(p1:PIXI.Point, p2:PIXI.Point):PIXI.Point {
        return new PIXI.Point(p1.x - p2.x, p1.y - p2.y);
    }

    static multiplyPoints(p1:PIXI.Point, p2:PIXI.Point):PIXI.Point {
        return new PIXI.Point(p1.x * p2.x, p1.y * p2.y);
    }

    static dividePoints(p1:PIXI.Point, p2:PIXI.Point):PIXI.Point {
        return new PIXI.Point(p1.x / p2.x, p1.y / p2.y);
    }

    static pointAdd(p1:PIXI.Point, value:number):PIXI.Point {
        return new PIXI.Point(p1.x + value, p1.y + value);
    }

    static pointSub(p1:PIXI.Point, value:number):PIXI.Point {
        return new PIXI.Point(p1.x - value, p1.y - value);
    }

    static pointMul(p1:PIXI.Point, value:number):PIXI.Point {
        return new PIXI.Point(p1.x * value, p1.y * value);
    }

    static pointDiv(p1:PIXI.Point, value:number):PIXI.Point {
        return new PIXI.Point(p1.x / value, p1.y / value);
    }


    static vectorFromAngle(a:number, magnitude:number = 1):PIXI.Point {
        return new PIXI.Point(Math.cos(a) * magnitude, Math.sin(a) * magnitude);
    }

    static angleFromVector(v:PIXI.Point):number {
        return Math.atan2(v.y, v.x);
    }


    static vectorMagnitude(p1:PIXI.Point):number {
        return Math.sqrt(p1.x * p1.x + p1.y * p1.y);
    }

    static normalizeVector(p1:PIXI.Point):PIXI.Point {
        const m = MathUtils.vectorMagnitude(p1);
        if(m > 0) {
            return MathUtils.pointDiv(p1, m);
        }
        return new PIXI.Point();
    }

    /**
     * A new PIXI.Point along a line defined by it's two end points
     * @param p1 first point
     * @param p2 second point
     * @param ratio The distance ratio from p1 to p2. Negative ratio is on the extended line before p1, ratio > 1 on extended line after p2. ratio > 0 && < 1 is on the line.
     * @returns {number} Distance
     */
    static pointAlongLine(p1:PIXI.Point, p2:PIXI.Point, ratio:number) {
        let t = ratio;
        return new PIXI.Point(((1 - t) * p1.x + t * p2.x), ((1 - t) * p1.y + t * p2.y));
    }

    /**
     * Returns distance between two points.
     * @param p1 first point
     * @param p2 second point
     * @returns {number} Distance
     */
    static distanceBetweenPoints(p1:PIXI.Point, p2:PIXI.Point) {
        return Math.sqrt((p1.x - p2.x) * (p1.x - p2.x) + (p1.y - p2.y) * (p1.y - p2.y));
    }

    /**
     * Creates a random point within a rectangle.
     *
     * @param rect The rectangle to get random numbers within, including edge values.
     * @param mask A rectangle that functions as mask, masking of the area from the fist rectangle.
     * So random points within the mask will be recalculated and ignored. You can use this to a variety of shapes.
     * @returns {PIXI.Point} Random point
     */
    static randomPosInRect(rect:PIXI.Rectangle, mask?:PIXI.Rectangle):PIXI.Point {
        let randomPos:PIXI.Point = new PIXI.Point(MathUtils.randomIntFromInterval(rect.x, rect.width + rect.x), MathUtils.randomIntFromInterval(rect.y, rect.height + rect.y));

        let missed:number = 0;
        if(mask) {
            while(mask.contains(randomPos.x, randomPos.y)) {
                randomPos = new PIXI.Point(MathUtils.randomIntFromInterval(rect.x, rect.width + rect.x), MathUtils.randomIntFromInterval(rect.y, rect.height + rect.y));
                missed++; //Only used for debugging, to se how many times the function have to recalculate the random point.
            }
        }
        return randomPos;
    }

    /**
     * Constrains a number to be within a range.
     *
     * Returns
     * value: if x is between min and max
     * min: if x is less than min
     * max: if x is greater than max
     *
     * @param value The value you want to constrain
     * @param min the minimum return value
     * @param max the maximum return value
     * @returns {number}
     */
    static constrain(value:number, min:number, max:number):number {
        return Math.max(min, Math.min(value, max));
    }

    static randomNumberFromInterval(min:number, max:number):number {
        let randomNumber:number = min + (max - min) * Math.random();
        return Number(randomNumber.toFixed(2));
    }

    static randomIntFromInterval(min:number, max:number):number {
        return Math.floor(Math.random() * (max - min + 1) + min);
    }

    static roundToDecimals(num:number, noOfDecimals:number):number {
        return Number((num).toFixed(noOfDecimals));
    }


    /**
     * @param points An array of [X, Y] coordinates
     */
    public convexHull(points:PIXI.Point[]):PIXI.Point[] {
        points.sort((a:PIXI.Point, b:PIXI.Point) => {
            return a.x == b.x ? a.y - b.y : a.x - b.x;
        });

        const lower:PIXI.Point[] = [];
        for(let i = 0; i < points.length; i++) {
            while(lower.length >= 2 && MathUtils.crossProduct(lower[lower.length - 2], lower[lower.length - 1], points[i]) <= 0) {
                lower.pop();
            }
            lower.push(points[i]);
        }

        const upper:PIXI.Point[] = [];
        for(let i = points.length - 1; i >= 0; i--) {
            while(upper.length >= 2 && MathUtils.crossProduct(upper[upper.length - 2], upper[upper.length - 1], points[i]) <= 0) {
                upper.pop();
            }
            upper.push(points[i]);
        }

        upper.pop();
        lower.pop();
        return lower.concat(upper);
    }

    /**
     * @param min min random number
     * @param max man random number
     * @returns {number}
     */
    public randomIntFromInterval(min:number, max:number):number {
        return Math.floor(Math.random() * (max - min + 1) + min);
    }

}